import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { uniqBy } from "lodash";
import { Suspense, useEffect, useMemo, useState } from "react";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import { useGetBundledRateOptionsQuery } from "@/hooks/__generated__/useGetBundledRateOptionsQuery.graphql";
import {
  getBundledRateTypeFromClientType,
  isGolfRound,
  useBundledRateOptionsQuery,
  useGetBundledRateOptionsQueryString,
} from "@/hooks/useGetBundledRateOptions";
import { getDynamicLockedRoundIdsAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/getDynamicLockedRoundIdsAtom/getDynamicLockedRoundIdsAtom";
import { isAlreadyPaidAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import {
  currentBookingAtom,
  currentSessionMemberShipTypeIdAtom,
  defaultMembershipTypeIdAtom,
} from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/stores";
import { PatronEditBooking } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/types";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { setPlayerRateOnRoundAtom } from "../../../../../../../../../../atoms/actions/setPlayerRateOnRound/setPlayerRateOnRoundAtom";
import { getCourseLayoutFromRoundIdAtom } from "../../../../../../../../../../atoms/selectors/getCourseLayoutFromRoundIdAtom/getCourseLayoutFromRoundIdAtom";
import { getRateFromRoundIdAtom } from "../../../../../../../../../../atoms/selectors/getRateFromRoundIdAtom/getRatefromRoundIdAtom";
import { getRoundFromIdAtom } from "../../../../../../../../../../atoms/selectors/getRoundFromIdAtom/getRoundFromIdAtom";
import {
  GolfPlayerRateControl_Booking$data,
  GolfPlayerRateControl_Booking$key,
} from "./__generated__/GolfPlayerRateControl_Booking.graphql";

const getMembershipTypeIdForPlayerId = (
  booking: GolfPlayerRateControl_Booking$data,
  playerId?: string | null
) => {
  if (!playerId) return null;

  const round = booking.rounds?.edges?.find(
    (edge) => edge?.node?.player?.id === playerId
  )?.node;

  return round?.player?.membershipType?.id ?? null;
};

const getMembershipTypeIdForRound = (
  booking: GolfPlayerRateControl_Booking$data,
  roundServerId?: string | null
) => {
  if (!roundServerId) return null;

  const round = booking.rounds?.edges?.find(
    (edge) => edge?.node?.id === roundServerId
  )?.node;

  return round?.player?.membershipType?.id ?? null;
};

type Props = {
  bookingRef: GolfPlayerRateControl_Booking$key;
  roundId: string;
};

export const GolfPlayerRateControl = ({ bookingRef, roundId }: Props) => {
  const booking = useFragment(GolfPlayerRateControlFragmentString, bookingRef);
  const [isTargetingAll, setIsTargetingAll] = useState(false);
  const currentSessionMemberShipTypeId = useAtomValue(
    currentSessionMemberShipTypeIdAtom
  );
  const defaultMembershipTypeId = useAtomValue(defaultMembershipTypeIdAtom);
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const currentRoundMembershipTypeId = (() => {
    if (!currentRound) return null;

    const clientType = currentRound.player.clientType;
    if (clientType === "customer") {
      return currentRound.player.membershipTypeId;
    }

    if (clientType === "unknownCustomer") {
      return defaultMembershipTypeId ?? currentSessionMemberShipTypeId ?? null;
    }

    if (clientType === "member") {
      return (
        currentRound.player.membershipTypeId ??
        getMembershipTypeIdForRound(booking, currentRound.serverId) ??
        currentSessionMemberShipTypeId ??
        null
      );
    }

    if (clientType === "memberHold") {
      return currentSessionMemberShipTypeId ?? null;
    }

    return (
      getMembershipTypeIdForPlayerId(booking, currentRound.hostId) ??
      currentSessionMemberShipTypeId ??
      null
    );
  })();
  const { queryRef } = useBundledRateOptionsQuery({
    timeSlotId: booking.startSlot?.id,
    membershipTypeId: currentRoundMembershipTypeId,
    visibleToMembershipTypeId: currentRoundMembershipTypeId,
    rateType: getBundledRateTypeFromClientType(currentRound?.player.clientType),
    nonPlayingHost: currentRound?.isNonPlayingHost ?? false,
  });

  if (!queryRef) {
    return (
      <VStack flex={1}>
        <SkeletonLoader borderRadius="2" width={"100%"} height={48} />
      </VStack>
    );
  }

  return (
    <Suspense
      fallback={
        <VStack flex={1}>
          <SkeletonLoader borderRadius="2" width={"100%"} height={48} />
        </VStack>
      }
    >
      <GolfPlayerRateControlInner
        booking={booking}
        queryRef={queryRef}
        roundId={roundId}
        isTargetingAll={isTargetingAll}
        setIsTargetingAll={setIsTargetingAll}
        currentSessionMemberShipTypeId={currentSessionMemberShipTypeId}
        defaultMembershipTypeId={defaultMembershipTypeId}
      />
    </Suspense>
  );
};

type InnerProps = {
  booking: GolfPlayerRateControl_Booking$data;
  queryRef: PreloadedQuery<useGetBundledRateOptionsQuery>;
  roundId: string;
  isTargetingAll: boolean;
  setIsTargetingAll: (value: boolean) => void;
  currentSessionMemberShipTypeId: string | null;
  defaultMembershipTypeId: string | null;
};

const GolfPlayerRateControlInner = ({
  booking,
  queryRef,
  roundId,
  isTargetingAll,
  setIsTargetingAll,
  currentSessionMemberShipTypeId,
  defaultMembershipTypeId,
}: InnerProps) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = usePreloadedQuery(useGetBundledRateOptionsQueryString, queryRef);
  const currentRate = useAtomValue(getRateFromRoundIdAtom(roundId));
  const currentCourseLayout = useAtomValue(
    getCourseLayoutFromRoundIdAtom(roundId)
  );
  const currentHoleCount = currentCourseLayout?.holeCount ?? "EIGHTEEN";
  const currentRateId = currentRate?.id;
  const setPlayerRateOnRound = useSetAtom(setPlayerRateOnRoundAtom);
  const isCurrentRateLocked = currentRate?.isLocked ?? false;
  const currentRound = useAtomValue(getRoundFromIdAtom(roundId));
  const currentBooking = useAtomValue(currentBookingAtom);
  const getRoundMembershipTypeId = (round: PatronEditBooking.Round | null) => {
    if (!round) return null;

    const clientType = round.player.clientType;
    if (clientType === "customer") {
      return round.player.membershipTypeId;
    }

    if (clientType === "unknownCustomer") {
      return defaultMembershipTypeId ?? currentSessionMemberShipTypeId ?? null;
    }

    if (clientType === "member") {
      return (
        round.player.membershipTypeId ??
        getMembershipTypeIdForRound(booking, round.serverId) ??
        currentSessionMemberShipTypeId ??
        null
      );
    }

    if (clientType === "memberHold") {
      return currentSessionMemberShipTypeId ?? null;
    }

    return (
      getMembershipTypeIdForPlayerId(booking, round.hostId) ??
      currentSessionMemberShipTypeId ??
      null
    );
  };
  const getRateEligibilityKey = (round: PatronEditBooking.Round | null) => {
    const membershipTypeId = getRoundMembershipTypeId(round);
    const rateType = getBundledRateTypeFromClientType(round?.player.clientType);
    const nonPlayingHost = round?.isNonPlayingHost ?? false;
    return `${membershipTypeId ?? "null"}|${rateType}|${nonPlayingHost}`;
  };
  const getRateEligibilityKeyIgnoringMembership = (
    round: PatronEditBooking.Round | null
  ) => {
    const rateType = getBundledRateTypeFromClientType(round?.player.clientType);
    const nonPlayingHost = round?.isNonPlayingHost ?? false;
    return `${rateType}|${nonPlayingHost}`;
  };
  const getTargetRateRoundIds = (rateId: string) => {
    if (!isTargetingAll || !currentRound || !currentBooking) {
      return undefined;
    }

    const selectedRateVisibility = data.slot?.rates?.find((playerRate) => {
      return playerRate?.rate?.id === rateId;
    })?.rate?.visibility;

    const sourceEligibilityKey =
      selectedRateVisibility === "ALL"
        ? getRateEligibilityKeyIgnoringMembership(currentRound)
        : getRateEligibilityKey(currentRound);

    return currentBooking.rounds
      .filter((round) => {
        const targetEligibilityKey =
          selectedRateVisibility === "ALL"
            ? getRateEligibilityKeyIgnoringMembership(round)
            : getRateEligibilityKey(round);
        return targetEligibilityKey === sourceEligibilityKey;
      })
      .map((round) => round.localId);
  };

  const dropdownOptions = useMemo(() => {
    const rates = data.slot?.rates ?? [];
    const allRates = rates.flatMap((playerRate) => {
      if (!playerRate?.rate?.id || !playerRate.rate.name) {
        return [];
      }

      return [
        {
          id: playerRate.rate.id,
          name: playerRate.rate.name,
          nineHolePrice: playerRate.nineHolePrice ?? 0,
          eighteenHolePrice: playerRate.eighteenHolePrice ?? 0,
        },
      ];
    });

    const bookedRate = isGolfRound(currentRound)
      ? currentRound.bookedRate
      : null;
    const roundRate = isGolfRound(currentRound) ? currentRound.roundRate : null;
    const activeRate = roundRate ?? bookedRate;
    const isActiveRateLocked = activeRate?.isLocked ?? false;

    const finalRates = Array.from(allRates).map((rate) => {
      if (bookedRate && rate.id === bookedRate.id && bookedRate.name) {
        return {
          id: bookedRate.id,
          name: bookedRate.name,
          nineHolePrice: bookedRate.nineHolePrice ?? rate.nineHolePrice ?? 0,
          eighteenHolePrice:
            bookedRate.eighteenHolePrice ?? rate.eighteenHolePrice ?? 0,
        };
      }

      if (roundRate && rate.id === roundRate.id && roundRate.name) {
        return {
          id: roundRate.id,
          name: roundRate.name,
          nineHolePrice: roundRate.nineHolePrice ?? rate.nineHolePrice ?? 0,
          eighteenHolePrice:
            roundRate.eighteenHolePrice ?? rate.eighteenHolePrice ?? 0,
        };
      }

      return rate;
    });

    const finalRateIds = new Set(finalRates.map((rate) => rate.id));
    const appendableRates = [bookedRate, roundRate].flatMap((rate) => {
      if (!rate?.id || !rate.name || finalRateIds.has(rate.id)) {
        return [];
      }

      return [
        {
          id: rate.id,
          name: rate.name,
          nineHolePrice: rate.nineHolePrice ?? 0,
          eighteenHolePrice: rate.eighteenHolePrice ?? 0,
        },
      ];
    });

    const options = uniqBy([...finalRates, ...appendableRates], "id").map(
      (rate) => {
        const priceInCents =
          currentHoleCount === "EIGHTEEN"
            ? rate.eighteenHolePrice
            : rate.nineHolePrice;

        return {
          value: rate.id,
          label: rate.name,
          subLabel: `${currencySymbol}${ConvertCentsToDollars(
            priceInCents ?? 0
          )}`,
        };
      }
    );

    if (isActiveRateLocked && activeRate?.id && activeRate.name) {
      const lockedPriceInCents =
        currentHoleCount === "EIGHTEEN"
          ? activeRate.eighteenHolePrice ?? 0
          : activeRate.nineHolePrice ?? 0;

      return [
        {
          value: activeRate.id,
          label: activeRate.name,
          subLabel: `${currencySymbol}${ConvertCentsToDollars(
            lockedPriceInCents
          )}`,
        },
      ];
    }

    return options;
  }, [data.slot?.rates, currentRound, currentHoleCount, currencySymbol]);

  const { hasDynamicLockedRound, isLockedDynamicRate } = useAtomValue(
    getDynamicLockedRoundIdsAtom(roundId)
  );
  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  useEffect(() => {
    const firstRateId = dropdownOptions[0]?.value;
    const ratesIncludeCurrentRate = dropdownOptions.some(
      (option) => option.value === currentRateId
    );

    if (
      firstRateId &&
      !isCurrentRateLocked &&
      (!currentRateId || !ratesIncludeCurrentRate)
    ) {
      setPlayerRateOnRound({
        roundId,
        rate: { id: firstRateId },
        isTargetingAll: false,
      });
    }
  }, [
    currentRateId,
    dropdownOptions,
    isCurrentRateLocked,
    roundId,
    setPlayerRateOnRound,
  ]);

  return dropdownOptions.length > 0 ? (
    <VStack flex={1}>
      <DropdownSelect
        hideOuterMargin
        isViewOnly={isCurrentRateLocked || isLockedDynamicRate || isAlreadyPaid}
        isDisabled={isLockedDynamicRate}
        hideChevron
        includeLeftElementInSelectedValue
        contentSizeOverride="regular"
        options={dropdownOptions}
        value={currentRateId ?? dropdownOptions[0]?.value}
        optionalToggle={
          hasDynamicLockedRound
            ? undefined
            : {
                initiallyChecked: isTargetingAll,
                onToggle: setIsTargetingAll,
              }
        }
        onChange={(rateId) => {
          const normalizedRateId = rateId ?? "";
          setPlayerRateOnRound({
            roundId,
            rate: { id: normalizedRateId },
            isTargetingAll,
            targetRoundIds: getTargetRateRoundIds(normalizedRateId),
          });
        }}
      />
    </VStack>
  ) : null;
};

const GolfPlayerRateControlFragmentString = graphql`
  fragment GolfPlayerRateControl_Booking on Booking {
    ... on StandardBooking {
      status
    }
    startSlot {
      id
      ... on TimeSlot {
        agenda {
          facility {
            type
          }
        }
      }
    }
    rounds {
      edges {
        node {
          id
          player {
            id
            ... on Customer {
              membershipType {
                id
              }
            }
            ... on Member {
              membershipType {
                id
              }
            }
          }
        }
      }
    }
  }
`;
